import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Check, Clock, Pencil } from 'lucide-react';
import type { ReactNode, SVGProps } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientButton } from '@/components/client-ui/button';
import { ClientWizardLayout } from '@/components/client-ui/ClientWizardLayout';
import { GlassCard } from '@/components/client-ui/glass-card';
import { ClientPanelSkeleton } from '@/components/client-ui/skeletons';
import { BookPractitionerSummaryCard } from '@/features/booking/components/BookPractitionerSummaryCard';
import { useClientCombinedSearch } from '@/features/booking/hooks/useClientSearch';
import { parseWizardSearch, requireSlot } from '@/features/booking/state/wizardSearch';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/client/book/details')({
  component: ClientBookDetailsStep,
  validateSearch: (s: Record<string, unknown>) => parseWizardSearch(s),
  beforeLoad: ({ search }) => {
    try {
      requireSlot(search);
    } catch {
      if (search.practitionerId === undefined) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: '/client/book/practitioner' });
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/client/book/when', search });
    }
  },
});

const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDateLong(iso: string): string {
  const [yStr, mStr, dStr] = iso.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const date = new Date(y, m - 1, d);
  const dow = WEEKDAYS_LONG[date.getDay()] ?? '';
  const month = MONTHS_LONG[m - 1] ?? '';
  return `${dow}, ${String(d)} ${month} ${String(y)}`;
}

function format12(hm: string): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0
    ? `${String(displayHour)} ${suffix}`
    : `${String(displayHour)}:${String(m).padStart(2, '0')} ${suffix}`;
}

function addMinutes(hm: string, min: number): string {
  const [hStr, mStr] = hm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const total = h * 60 + m + min;
  const nh = Math.floor(total / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

function toMinutes(hm: string): number {
  const [hStr, mStr] = hm.split(':');
  return Number(hStr) * 60 + Number(mStr);
}

/**
 * Slot shape we need for mode availability — narrowed to the fields used so
 * the helper is testable without the full searchSlotSchema. Mirrors the BE
 * `searchSlotSchema.mode = z.enum(['online','offline','both'])`.
 */
interface SlotForMode {
  startTime: string;
  endTime: string;
  mode: 'online' | 'offline' | 'both';
}

/**
 * Compute which session modes the user can pick, given the BE slots and the
 * booking window the user chose in the When step. Returns the empty set
 * when no slot covers the booking — which happens transiently while the
 * search query is still loading.
 *
 *   - slot.mode === 'online'  → 'online' available
 *   - slot.mode === 'offline' → 'in_person' available
 *   - slot.mode === 'both'    → both available
 *
 * Multiple covering slots union together (e.g. an online-only block and an
 * offline-only block at the same time window expose both modes).
 */
function computeAvailableModes(
  slots: readonly SlotForMode[],
  startTime: string,
  durationMinutes: number,
): Set<'online' | 'in_person'> {
  const modes = new Set<'online' | 'in_person'>();
  if (slots.length === 0 || startTime === '') return modes;
  const bookingStart = toMinutes(startTime);
  const bookingEnd = bookingStart + durationMinutes;
  for (const s of slots) {
    const sStart = toMinutes(s.startTime);
    const sEnd = toMinutes(s.endTime);
    if (sStart > bookingStart || sEnd < bookingEnd) continue;
    if (s.mode === 'online' || s.mode === 'both') modes.add('online');
    if (s.mode === 'offline' || s.mode === 'both') modes.add('in_person');
  }
  return modes;
}

function ClientBookDetailsStep() {
  const { t } = useTranslation('clientBooking');
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [chosenMode, setChosenMode] = useState<'online' | 'in_person' | null>(
    search.sessionMode ?? null,
  );
  const [note, setNote] = useState<string>(search.note ?? '');

  const date = search.date ?? '';
  const startTime = search.startTime ?? '';
  const duration = search.durationMinutes ?? 60;

  // Pull the practitioner from the same availability search the When step uses
  // so the summary card shows the avatar + name + role instead of a raw ID.
  const searchQuery = useClientCombinedSearch({ date, mode: undefined });
  const own = (searchQuery.data?.results ?? []).find(
    (r) => r.practitioner.id === search.practitionerId,
  );

  // BE returns one slot per assignment with `mode ∈ { online | offline | both }`.
  // The user picked a startTime in the When step, but the available session
  // modes are determined by which BE slot(s) cover the booking window. A `both`
  // block returns mode='both', meaning the slot is bookable as either online OR
  // offline. If only `online` slots cover the booking, the In-person tile must
  // be hidden — otherwise the confirm POST will 409 with "Practitioner is not
  // available for this time / location".
  const availableModes = computeAvailableModes(own?.slots ?? [], startTime, duration);

  // Effective mode:
  //   - data not yet loaded (no slots resolved): allow the user's previous pick
  //     to stand, no auto-default. Continue stays disabled until data arrives.
  //   - exactly one available: force-select it (no choice to make).
  //   - multiple available + chosen still valid: keep chosen.
  //   - multiple available + chosen invalid/unset: null (user must pick).
  const mode: 'online' | 'in_person' | null = (() => {
    if (availableModes.size === 0) return chosenMode;
    if (availableModes.size === 1) {
      const only = Array.from(availableModes)[0];
      return only ?? null;
    }
    if (chosenMode !== null && availableModes.has(chosenMode)) return chosenMode;
    return null;
  })();

  const onBack = () => {
    void navigate({ to: '/client/book/when', search });
  };

  const onContinue = () => {
    if (mode === null) return;
    void navigate({
      to: '/client/book/confirm',
      search: {
        ...search,
        sessionMode: mode,
        sessionType: search.sessionType ?? 'new',
        note: note.trim().length > 0 ? note.trim() : undefined,
      },
    });
  };

  return (
    <ClientWizardLayout current={3} contentMaxWidth="narrow">
      <div className="mx-auto flex w-full max-w-175 flex-col items-stretch gap-2.5">
        <BookPractitionerSummaryCard
          practitioner={own !== undefined ? own.practitioner : null}
          eyebrow={t('wizard.step.when.summaryEyebrow')}
          changeLabel={t('wizard.step.when.change')}
          onChange={() => {
            void navigate({ to: '/client/book/practitioner', search });
          }}
        />

        <DateTimeRow
          dateLine={formatDateLong(date)}
          timeLine={`${format12(startTime)} – ${format12(addMinutes(startTime, duration))} (${String(duration)} MINS)`}
          onChange={onBack}
          changeLabel={t('wizard.step.when.change')}
        />

        {/* Session-mode card — PANEL, not glass. Its ModeTile children carry
            `bg-[var(--color-client-surface)]` (opaque white) when unselected and
            an opaque primary tint when selected, so a 62%-translucent parent
            would sit UNDER its own children — the exact inversion this pass is
            fixing. `bg-client-panel rounded-client-xl` composes the opaque fill
            with the r24 the Figma frame pins this card to; GlassCard's `panel`
            variant is bolted to rounded-client-panel (r30) and both radius
            classes would survive cn(), so the utilities are composed directly to
            keep exactly one radius on the element. */}
        <div className="bg-client-panel rounded-client-xl w-full p-4 sm:p-5">
          <Eyebrow>{t('wizard.step.details.sectionMode')}</Eyebrow>
          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:mt-5 sm:grid-cols-2">
            {/* Both tiles below are gated on `availableModes`, which cannot be
                populated until the availability query resolves — so without
                this branch the card rendered its eyebrow over an empty box for
                the whole request. Two placeholders because two is the maximum
                and the grid is 2-up at sm+, so the card is its final height
                immediately and nothing below it shifts when the modes land. */}
            {searchQuery.isLoading ? (
              <div data-testid="mode-tiles-loading" className="contents">
                <ClientPanelSkeleton className="rounded-client-lg h-23" lines={1} heading={false} />
                <ClientPanelSkeleton className="rounded-client-lg h-23" lines={1} heading={false} />
              </div>
            ) : null}
            {availableModes.has('online') ? (
              <ModeTile
                selected={mode === 'online'}
                onClick={() => {
                  setChosenMode('online');
                }}
                title={t('wizard.step.details.modeOnlineTitle')}
                description={t('wizard.step.details.modeOnlineDescription')}
                icon={<VideoSolidIcon className="h-5 w-5" aria-hidden />}
              />
            ) : null}
            {availableModes.has('in_person') ? (
              <ModeTile
                selected={mode === 'in_person'}
                onClick={() => {
                  setChosenMode('in_person');
                }}
                title={t('wizard.step.details.modeInPersonTitle')}
                description={t('wizard.step.details.modeInPersonDescription')}
                icon={<UsersSolidIcon className="h-5 w-5" aria-hidden />}
              />
            ) : null}
          </div>
        </div>

        {/* Note card — PANEL. Measured at 375 before this change: the card's own
            edge was +16/+16/+17 against the wash while its child <textarea>
            (1px #dcdcdc) was -35/-35/-35 against the card, i.e. the child
            out-contrasted its parent by more than 2x. The fix is to give the
            parent an ELEVATION channel the child does not have (the panel's
            3-stop shadow + 135deg ring) rather than to thicken the parent's
            border and start a border arms-race with its own input. */}
        <div className="bg-client-panel rounded-client-xl w-full p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
            <Eyebrow>{t('wizard.step.details.sectionNote')}</Eyebrow>
            <span className="font-client-display text-[12px] leading-6 tracking-[var(--tracking-client-mono-eyebrow)] text-[var(--color-client-muted-foreground-soft)]">
              {String(note.length)}/500
            </span>
          </div>
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value.slice(0, 500));
            }}
            rows={4}
            maxLength={500}
            placeholder={t('wizard.step.details.notePlaceholder')}
            // The focus ring is the SHARED TOKEN, not a hand-rolled shadow.
            // What was here — `shadow-[0_0_0_4px_var(--color-client-primary-50)]`
            // — got three things wrong at once, measured at DPR 4 walking
            // outward from the border edge: 16 device px (4.00 CSS px) of
            // #f3f7f9, FLUSH against the border. No gap; 4px instead of 3px;
            // and #f3f7f9 is the washed-out primary-50 that client-theme.css
            // itself rejects as "Δwhite 15.6 -> washed out".
            // `--shadow-client-focus-ring` is `0 0 0 1px #ffffff, 0 0 0 3px
            // #dfeaf1`, which paints 1.00 CSS px of white then 2.00 CSS px of
            // #dfeaf1 — the white layer is drawn ON TOP of the first px of
            // the pale one, so the total is 3px, not 4. That is byte-for-byte
            // the auth email/password field shell, which is the reference.
            // No resting box-shadow on this textarea, so nothing is lost by
            // replacing the property wholesale.
            className="font-client-sans block w-full resize-none rounded-[14px] border border-[var(--color-client-line-300)] bg-[var(--color-client-surface)] px-4 py-3 text-[14px] leading-5 text-[var(--color-client-foreground)] transition-[border-color,box-shadow] duration-150 ease-out outline-none placeholder:text-[rgba(34,34,34,0.5)] hover:border-[var(--color-client-line-400)] focus-visible:border-[var(--color-client-primary-500)] focus-visible:shadow-(--shadow-client-focus-ring) sm:px-5.25 sm:py-4.5 sm:text-[16px] sm:leading-6"
          />
        </div>

        <div className="mt-2 flex items-center justify-center gap-2 sm:gap-2.5">
          <ClientButton
            variant="secondary"
            onClick={onBack}
            className="h-10 flex-1 rounded-[12px] sm:w-35 sm:flex-initial"
          >
            {t('wizard.step.details.back')}
          </ClientButton>
          <ClientButton
            variant="primary"
            onClick={onContinue}
            disabled={mode === null}
            className="h-10 flex-1 rounded-[12px] sm:w-35 sm:flex-initial"
          >
            {t('wizard.step.details.continue')}
          </ClientButton>
        </div>
      </div>
    </ClientWizardLayout>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="font-client-display block text-[14px] leading-6 font-medium tracking-[var(--tracking-client-mono-eyebrow)] text-[var(--color-client-foreground)] uppercase">
      {children}
    </span>
  );
}

function DateTimeRow({
  dateLine,
  timeLine,
  onChange,
  changeLabel,
}: {
  dateLine: string;
  timeLine: string;
  onChange: () => void;
  changeLabel: string;
}) {
  return (
    // Glass, matching BookPractitionerSummaryCard directly above it: both rows
    // are "already decided, tap to change", so they have to share one treatment
    // or the step reads as unrelated card systems stacked. Flat before: the
    // right edge was a +2/+1/+1 step at 375 with no border and no shadow.
    // This one is a deliberate trade-off, not an oversight. A/B at 375,
    // edge-vs-wash max per channel L/T/R:
    //     flat 20/13/2   glass 16/19/33   panel 25/24/23
    // Panel wins the left and top edges, glass wins the right by 10. Both are
    // inside the band the dashboard's own cards occupy (13-29), so coherence
    // with the summary row above — which glass wins outright, 15/26/32 vs
    // 13/20/23 — decides it rather than a 3-unit edge difference.
    // `rounded-[24px]` is dropped, not kept — GlassCard already supplies
    // rounded-client-xl (24px) and tailwind-merge would keep both.
    <GlassCard className="flex w-full items-center justify-between gap-3 p-4 sm:gap-5 sm:py-5 sm:pr-5 sm:pl-7">
      <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:gap-3">
        <Clock
          className="h-5 w-5 shrink-0 text-[var(--color-client-primary-500)] sm:h-6 sm:w-6"
          aria-hidden
          strokeWidth={1.5}
        />
        <div className="flex min-w-0 flex-col">
          <span className="text-[13px] leading-5 text-[var(--color-client-muted-foreground-soft)] sm:text-[14px] sm:leading-6">
            {dateLine}
          </span>
          <span className="text-[14px] leading-5 font-medium text-[var(--color-client-foreground)] sm:text-[16px] sm:leading-6">
            {timeLine}
          </span>
        </div>
      </div>
      <ClientButton
        type="button"
        variant="link"
        onClick={onChange}
        aria-label={changeLabel}
        className="shrink-0 gap-1.5 font-semibold sm:gap-2"
      >
        <Pencil className="h-4 w-4" aria-hidden />
        {/* Pencil-only on mobile so the date+time column has room to lay
            out 'Wednesday, 13 May 2026 · 12 PM – 12:45 PM (45 MINS)' on
            one or two lines instead of three at 320px. The aria-label
            above preserves the action's name for AT users. */}
        <span className="hidden sm:inline">{changeLabel}</span>
      </ClientButton>
    </GlassCard>
  );
}

function ModeTile({
  selected,
  onClick,
  title,
  description,
  icon,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-3 rounded-[14px] border p-4 text-left transition-colors sm:gap-5 sm:py-5 sm:pr-8 sm:pl-5',
        selected
          ? 'border-[var(--color-client-primary-500)] bg-[var(--color-client-primary-tint-soft)]'
          : 'border-[var(--color-client-line-300)] bg-[var(--color-client-surface)]',
      )}
    >
      <span
        className={cn(
          'relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] sm:h-12.5 sm:w-12.5 sm:rounded-[14px]',
          selected
            ? 'bg-[var(--color-client-primary-tint-deep)] text-[var(--color-client-primary-500)]'
            : 'bg-[var(--color-client-line-100)] text-[var(--color-client-muted-foreground-soft)]',
        )}
      >
        {icon}
        {selected ? (
          <span className="absolute -top-1.5 -right-1.5 inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[var(--color-client-primary-500)] text-white">
            <Check className="h-[14px] w-[14px]" aria-hidden strokeWidth={2.5} />
          </span>
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            'text-[16px] leading-[26px] font-semibold',
            selected
              ? 'text-[var(--color-client-primary-500)]'
              : 'text-[var(--color-client-foreground)]',
          )}
        >
          {title}
        </span>
        <span
          className={cn(
            'text-[14px] leading-5 tracking-[0.25px]',
            selected
              ? 'text-[var(--color-client-foreground)]'
              : 'text-[var(--color-client-muted-foreground-mid)]',
          )}
        >
          {description}
        </span>
      </span>
    </button>
  );
}

// Solid (filled) video camera matching /public/online.svg. Uses currentColor so
// the tile's text-color drives whether the icon reads green (selected) or grey
// (unselected) — same pattern as the Figma 2954:3226 mode tiles.
function VideoSolidIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M3.75 3.75C3.08696 3.75 2.45107 4.01339 1.98223 4.48223C1.51339 4.95107 1.25 5.58696 1.25 6.25V13.75C1.25 14.413 1.51339 15.0489 1.98223 15.5178C2.45107 15.9866 3.08696 16.25 3.75 16.25H10.625C11.288 16.25 11.9239 15.9866 12.3928 15.5178C12.8616 15.0489 13.125 14.413 13.125 13.75V6.25C13.125 5.58696 12.8616 4.95107 12.3928 4.48223C11.9239 4.01339 11.288 3.75 10.625 3.75H3.75ZM16.6167 15.625L14.375 13.3833V6.61667L16.6167 4.375C17.4033 3.5875 18.75 4.145 18.75 5.25833V14.7417C18.75 15.855 17.4033 16.4125 16.6167 15.625Z" />
    </svg>
  );
}

// Solid (filled) people icon matching /public/offline.svg.
function UsersSolidIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M3.75 5.3125C3.75 4.40082 4.11216 3.52648 4.75682 2.88182C5.40148 2.23716 6.27582 1.875 7.1875 1.875C8.09918 1.875 8.97352 2.23716 9.61818 2.88182C10.2628 3.52648 10.625 4.40082 10.625 5.3125C10.625 6.22418 10.2628 7.09852 9.61818 7.74318C8.97352 8.38784 8.09918 8.75 7.1875 8.75C6.27582 8.75 5.40148 8.38784 4.75682 7.74318C4.11216 7.09852 3.75 6.22418 3.75 5.3125ZM11.875 7.1875C11.875 6.81816 11.9477 6.45243 12.0891 6.1112C12.2304 5.76997 12.4376 5.45993 12.6988 5.19876C12.9599 4.9376 13.27 4.73043 13.6112 4.58909C13.9524 4.44775 14.3182 4.375 14.6875 4.375C15.0568 4.375 15.4226 4.44775 15.7638 4.58909C16.105 4.73043 16.4151 4.9376 16.6762 5.19876C16.9374 5.45993 17.1446 5.76997 17.2859 6.1112C17.4273 6.45243 17.5 6.81816 17.5 7.1875C17.5 7.93342 17.2037 8.64879 16.6762 9.17624C16.1488 9.70368 15.4334 10 14.6875 10C13.9416 10 13.2262 9.70368 12.6988 9.17624C12.1713 8.64879 11.875 7.93342 11.875 7.1875ZM1.25 15.9375C1.25 14.3628 1.87556 12.8526 2.98905 11.7391C4.10255 10.6256 5.61278 10 7.1875 10C8.76222 10 10.2724 10.6256 11.3859 11.7391C12.4994 12.8526 13.125 14.3628 13.125 15.9375V15.94L13.1242 16.0392C13.1224 16.1451 13.0937 16.2489 13.0408 16.3408C12.9879 16.4326 12.9125 16.5095 12.8217 16.5642C11.121 17.5883 9.17272 18.128 7.1875 18.125C5.1275 18.125 3.19917 17.555 1.55417 16.5642C1.46321 16.5096 1.38764 16.4328 1.33457 16.3409C1.28151 16.2491 1.25269 16.1452 1.25083 16.0392L1.25 15.9375ZM14.375 15.94L14.3742 16.06C14.3695 16.3378 14.3032 16.611 14.18 16.86C15.6348 16.9497 17.0878 16.6597 18.3967 16.0183C18.4979 15.9689 18.5838 15.8929 18.6455 15.7986C18.7071 15.7043 18.742 15.5951 18.7467 15.4825C18.7761 14.7835 18.6245 14.0888 18.3066 13.4656C17.9887 12.8424 17.5153 12.3119 16.9321 11.9254C16.349 11.5389 15.6759 11.3096 14.9781 11.2596C14.2803 11.2096 13.5815 11.3406 12.9492 11.64C13.8768 12.8805 14.3769 14.3885 14.3742 15.9375L14.375 15.94Z" />
    </svg>
  );
}
